import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    FormContainer,
    DataForm,
    JPEG_FILE_EXTENSION,
    JPEG_FILE_TYPE_DROPZONE,
    JPG_FILE_EXTENSION,
    JPG_FILE_TYPE_DROPZONE,
    PNG_FILE_EXTENSION,
    PNG_FILE_TYPE_DROPZONE,
    PageContainerActionParamType,
    Loader,
    DataFormFieldChangeParamType,
    SaveDataParamType,
    useStateContext,
} from "@vegi.toenlioe/novitate-react-components";
import Divisi from '../../../models/Divisi';
import { validate as uuidValidate } from "uuid";
import { MenuItems } from '../../../utils/Menu';

const groupName: string = "Umum";
const subgroupName: string = "Master";
const code: string = "GLOBAL-DIVISI";
const title: string = "Divisi";
const endpoint = "divisi";

function Form() {
	const location = useLocation();
    const {apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState<Divisi>({ kode: '', nama: '' });
    const navigate = useNavigate();
    
    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
            return;

        setLoading(true);
		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response: any) => setFormData({...response.data?.data}))
            .catch((error: any) => console.error({error}))
            .finally(() => setLoading(false))
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onValidate = useCallback((data: Divisi | undefined) => {
        return true;
    }, []);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<Divisi> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const apiCallerObj = param.data.ucode ? apiCaller.put(endpoint, JSON.stringify(param.data)) : apiCaller.post(endpoint, JSON.stringify(param.data));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error: any) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);

    return (
        <>
        {
            loading && <Loader />
        }
            <FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
                <DataForm<Divisi>
                    menuItems={MenuItems}
                    groupName={groupName}
                    subgroupName={subgroupName}
                    code={code}
                    title={title}
                    data={formData}
                    isViewMode={isViewMode}
                    fields={[
                        {
                            name: "kode",
                            title: "Kode",
                            inputType: "text",
                            dataType: (typeof formData.kode),
                            isReadonly: false,
                            isRequired: formData?.ucode ? true : false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Divisi>) => onChanged(valueObj),
                        },
                        {
                            name: "nama",
                            title: "Nama",
                            inputType: "text",
                            dataType: (typeof formData.nama),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Divisi>) => onChanged(valueObj),
                        },
                        {
                            name: "kota",
                            title: "Kota",
                            inputType: "text",
                            dataType: (typeof formData.kota),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Divisi>) => onChanged(valueObj),
                        },
                        {
                            name: "tlp",
                            title: "Telepon/HP",
                            inputType: "text",
                            dataType: (typeof formData.tlp),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Divisi>) => onChanged(valueObj),
                        },
                        {
                            name: "logo",
                            title: "Logo",
                            inputType: "file",
                            dataType: (typeof formData.logo),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Divisi>) => onChanged(valueObj),
                            keyField: "logo",
                            fileNameField: "nama_file_logo",
                            fileTypeField: "tipe_file_logo",
                            isSingleFile: true,
                            acceptedFileTypes: {
                                [PNG_FILE_TYPE_DROPZONE]: [PNG_FILE_EXTENSION],
                                [JPG_FILE_TYPE_DROPZONE]: [JPG_FILE_EXTENSION],
                                [JPEG_FILE_TYPE_DROPZONE]: [JPEG_FILE_EXTENSION],
                            },
                        },
                        {
                            name: "alamat",
                            title: "Alamat",
                            inputType: "textarea",
                            dataType: (typeof formData.alamat),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Divisi>) => onChanged(valueObj),
                            rows: 3,
                        },
                    ]}
                    onValidate={onValidate}
                    onClose={(pathObject) => onClose(pathObject)}
                    onSave={(data) => onSave(data)}
                />
            </FormContainer>
        </>
    );
};

export default Form;
